import { clientSideCookies } from '@tv2/lib-offspring/libs/clientSideCookies';
import { useCallback, useState } from 'react';
import { PanelContainer } from '../AdminPanel';
import { useActiveExperiments } from '../api/useActiveExperiments';

type ExperimentSelection = Record<string, string>;
export const PCS_EXPERIMENTAL_COOKIE = 'play_experiments';
const NONE_SELECTED = 'none';

function encodeExperimentsCookie(sel: ExperimentSelection): string {
  return Object.entries(sel)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join(',');
}

function decodeExperimentsCookie(s?: string | null): ExperimentSelection {
  if (!s) return {};
  const out: ExperimentSelection = {};
  const decoded = decodeURIComponent(s);
  decoded.split(',').forEach(part => {
    const [k, v] = part.split('=', 2);
    if (k && v) {
      out[decodeURIComponent(k)] = v ? decodeURIComponent(v) : '';
    }
  });
  return out;
}

function getDecodedExperimentsFromCookies(): ExperimentSelection {
  return decodeExperimentsCookie(clientSideCookies.getCookie(PCS_EXPERIMENTAL_COOKIE));
}

export function encodeExperimentsFromCookies(): string {
  return encodeExperimentsCookie(getDecodedExperimentsFromCookies());
}

export function FeatureFlagsPanel() {
  const { experiments, fetching } = useActiveExperiments();

  const cookieExperiments = getDecodedExperimentsFromCookies();

  const [experimentSelection, setExperimentalSelection] =
    useState<ExperimentSelection>(cookieExperiments);

  const setExperimentalCookie = useCallback((next: ExperimentSelection) => {
    clientSideCookies.setCookie({
      key: PCS_EXPERIMENTAL_COOKIE,
      value: encodeExperimentsCookie(next),
      maxAge: 60 * 60 * 24 * 30,
      secure: false,
    });
  }, []);

  const onChange = useCallback(
    (key: string, val: string) => {
      const next = { ...experimentSelection };
      next[key] = val === NONE_SELECTED ? '' : val;
      setExperimentalSelection(next);
      setExperimentalCookie(next);
    },
    [experimentSelection, setExperimentalCookie]
  );

  return (
    <PanelContainer>
      <h2 className="text-L">PCS FeatureFlags</h2>
      {fetching ? (
        <div>fetching...</div>
      ) : (
        <div className="flex flex-col space-y-4">
          {experiments
            .sort((a, b) => a.name.length - b.name.length)
            .map(e => (
              <div key={e.variants + e.name}>
                <h3 className="text-M">Experiment:</h3>
                <p>Name: {e.name}</p>
                <p>Location: {e.location}</p>
                <p>Variants: </p>
                <fieldset className="min-w-max grid grid-cols-2">
                  <div className="flex flex-row space-x-1">
                    <input
                      type="radio"
                      id={NONE_SELECTED}
                      name={e.name}
                      value={NONE_SELECTED}
                      checked={!experimentSelection[e.name]}
                      onChange={() => onChange(e.name, NONE_SELECTED)}
                    />
                    <label htmlFor={NONE_SELECTED} className="flex flex-row space-x-1">
                      default
                    </label>
                  </div>
                  {e.variants.map(v => (
                    <div key={v} className="flex flex-row space-x-1">
                      <input
                        type="radio"
                        id={v}
                        name={e.name}
                        value={v}
                        checked={experimentSelection[e.name] === v}
                        onChange={() => onChange(e.name, v)}
                      />
                      <label htmlFor={v}>{v}</label>
                    </div>
                  ))}
                </fieldset>
              </div>
            ))}
        </div>
      )}
    </PanelContainer>
  );
}
